import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, ShoppingCart, X, Instagram } from "lucide-react";
import CookieConsent from "./CookieConsent";
import EhoBadge from "./EhoBadge";
import { setAnalyticsEnabled } from "../features/analytics/tracker";
import RouteAnalytics from "../features/analytics/RouteAnalytics";

export default function Layout({ children }) {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [cartBump,  setCartBump]  = useState(false);
  const [scrolled,  setScrolled]  = useState(false);

  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("cart");
      if (!stored) return setCartCount(0);
      const items  = JSON.parse(stored) || [];
      setCartCount(items.reduce((n, it) => n + (Number(it.qty) || 0), 0));
    } catch { setCartCount(0); }
  }, [location]);

  useEffect(() => {
    const onUpdate = e => {
      const count = e?.detail?.count;
      if (typeof count === "number") {
        setCartCount(count);
        setCartBump(true);
        setTimeout(() => setCartBump(false), 300);
      }
    };
    window.addEventListener("cartUpdated", onUpdate);
    return () => window.removeEventListener("cartUpdated", onUpdate);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") closeMenu(); };
    if (menuOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, closeMenu]);

  const handleMenuNavigation = path => {
    setMenuOpen(false);
    setIsFadingOut(true);
    setTimeout(() => { navigate(path); setIsFadingOut(false); }, 500);
  };

  const drawer = {
    initial: { x: "-100%" },
    animate: { x: 0,       transition: { duration: 0.1, ease: "easeOut" } },
    exit:    { x: "-100%", transition: { duration: 0.2 } },
  };

  return (
    <div className="site-shell">
      {/* HEADER */}
      <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
        <button className="icon-btn" aria-label="Open menu" aria-haspopup="dialog" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
          <Menu size={28} />
        </button>
        <Link to="/" className="brand-link" aria-label="Go to home">
          <img src="/images/logo/logo.svg" alt="" className="brand-logo" />
        </Link>
        <Link to="/cart" className={`icon-btn cart-btn ${cartBump ? "bump" : ""}`} aria-label={`Cart${cartCount ? `, ${cartCount} items` : ""}`}>
          <ShoppingCart size={28} />
          {cartCount > 0 && <span className="cart-badge">{Math.round(cartCount)}</span>}
        </Link>
      </header>

      {/* DRAWER + BACKDROP */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.aside className="drawer drawer--frosted" role="dialog" aria-modal="true" aria-label="Main menu" {...drawer}>
              <div className="drawer-header">
                <button className="icon-btn" onClick={closeMenu} aria-label="Close menu"><X size={28} /></button>
              </div>

              <div className="drawer-hero">
                <Link to="/"><img src="/images/logo/logo.svg" alt="" className="brand-logo" /></Link>
              </div>

              <nav className="drawer-nav">
                <ul className="drawer-list">
                  {[
                    { label: "Browse Products",    to: "/browse-products", icon: "🛒" },
                    { label: "Subscribe",          to: "/subscribe",       icon: "🔄" },
                    { label: "How it Works",       to: "/how-it-works",   icon: "❓" },
                    { label: "Partner With Us",    to: "/partner",        icon: "🤝" },
                    { label: "The Story (so far)", to: "/about",          icon: "🌱" },
                    { label: "Get in Touch",       to: "/contact",        icon: "✉️" },
                  ].map(({ label, to, icon }) => {
                    const active = location.pathname.startsWith(to);
                    return (
                      <motion.li key={to} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
                        <button
                          onClick={() => handleMenuNavigation(to)}
                          className={`drawer-link drawer-link--rich ${active ? "is-active" : ""}`}
                        >
                          <span className="dl-icon" aria-hidden>{icon}</span>
                          <span className="dl-label">{label}</span>
                          <span className="dl-chevron" aria-hidden>›</span>
                        </button>
                      </motion.li>
                    );
                  })}
                </ul>
              </nav>

              <div className="drawer-cta">
                <button className="btn btn-primary btn--wide" onClick={() => handleMenuNavigation("/browse-products")}>
                  Shop Now
                </button>
              </div>

              <div className="drawer-footer">
                <div className="drawer-social">
                  <a href="https://www.instagram.com/teardrop_hydro/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="soc"><Instagram size={22} /></a>
                  <a href="mailto:contact@teardrophydro.co.uk"         aria-label="Email"     className="soc">@</a>
                </div>
              </div>
            </motion.aside>

            <motion.button
              className="backdrop"
              aria-label="Close menu overlay"
              onClick={closeMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.35 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          </>
        )}
      </AnimatePresence>

      {/* MAIN */}
      <motion.main
        key={location.pathname}
        className="site-main"
        animate={{ opacity: isFadingOut ? 0 : menuOpen ? 0.3 : 1, scale: menuOpen ? 0.98 : 1 }}
        transition={{ duration: 0.25 }}
        style={{ pointerEvents: menuOpen ? "none" : "auto" }}
      >
        {children}
      </motion.main>

      {/* FOOTER */}
      <footer className="site-footer">
        <a
          href="https://www.instagram.com/teardrop_hydro/"
          target="_blank"
          rel="noopener noreferrer"
          className="footer-instagram"
        >
          <Instagram size={18} />
          Follow us on Instagram <span className="footer-instagram__handle">@teardrop_hydro</span>
        </a>
        <p className="muted" style={{ margin: "0 0 var(--s-3)", fontSize: "var(--fs-13)" }}>
          © 2026 TearDrop Hydroponics •{" "}
          <Link to="/privacy-policy" className="footer-link">Privacy&nbsp;Policy</Link>
              </p>
              <EhoBadge size="md" showLabel={false} />
      </footer>

      <RouteAnalytics />
      <CookieConsent onConsentChange={consent => setAnalyticsEnabled(!!consent.analytics)} />
    </div>
  );
}